/**
 * Google Analytics for the app, with Discord IDs kept out of it.
 *
 * gtag() only queues commands: index.html defines it with every consent
 * default denied, and /consent.js loads gtag.js unless the visitor opted out.
 * Every function here is a no-op when gtag is missing (ad blockers, tests)
 * and none of them throws.
 *
 * Dashboard URLs carry Discord server IDs (and sometimes user or role IDs),
 * and dashboard titles carry server names, none of which may reach Google, so
 * every path, URL and title sent is redacted first.
 * index.html turns gtag's automatic page_view off; main.tsx calls
 * trackPageView on the first load and after every navigation.
 */

type Gtag = (...args: unknown[]) => void;

export type ConsentChoice = "granted" | "denied";

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
    /** Defined by /consent.js (web/public/consent.js). */
    rlConsent?: {
      get(): ConsentChoice;
      set(choice: ConsentChoice): void;
    };
  }
}

/** A Discord snowflake: 17 to 20 digits, not part of a longer number. */
const SNOWFLAKE = /(^|\D)\d{17,20}(?!\d)/g;

const redactIds = (text: string): string => text.replace(SNOWFLAKE, "$1:id");

/** "/dashboard/123456789012345678/logs" becomes "/dashboard/:id/logs". */
export function redactPath(pathname: string): string {
  return redactIds(pathname);
}

/**
 * The URL as Google Analytics may see it: origin, redacted path and the
 * utm_* campaign parameters. Every other parameter (bot_added, plugin setup
 * params) and the hash are dropped.
 */
export function sanitizeLocation(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    // Not an absolute URL: keep only its redacted path.
    return redactPath(href.split(/[?#]/)[0]);
  }
  const utm = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (key.startsWith("utm_")) utm.append(key, redactIds(value));
  });
  const query = utm.toString();
  return `${url.origin}${redactPath(url.pathname)}${query ? `?${query}` : ""}`;
}

const DASHBOARD_TITLE = "Dashboard | RoleLogic";

/**
 * The page title as Google Analytics may see it. A server's pages are titled
 * "<server name> - Dashboard | RoleLogic" (SidebarLayout), and a page that
 * renders no <SEO> while it loads (/upgrade, /dashboard) keeps the previous
 * page's title, so a server name can be in document.title on any route.
 * Server names are Discord data too: such titles become the plain dashboard
 * title.
 */
export function sanitizeTitle(pathname: string, title: string): string {
  return /^\/dashboard\/[^/]/.test(pathname) ||
    / - Dashboard \| RoleLogic$/.test(title)
    ? DASHBOARD_TITLE
    : title;
}

/** Runs `send` with the page's gtag, if any. Analytics never breaks the app. */
function withGtag(send: (gtag: Gtag) => void): void {
  const gtag = typeof window === "undefined" ? undefined : window.gtag;
  if (typeof gtag !== "function") return;
  try {
    send(gtag);
  } catch {
    // Swallowed on purpose: a failed analytics call is not the user's problem.
  }
}

/**
 * A page_view for the current URL. Also sets page_location and page_title
 * globally: the events gtag.js sends by itself (user_engagement, scroll, ...)
 * and the parameter-less bot_added would otherwise read the raw URL and
 * document.title when they are sent.
 */
export function trackPageView(): void {
  withGtag((gtag) => {
    const { location, document } = window;
    const page_location = sanitizeLocation(location.href);
    const page_title = sanitizeTitle(location.pathname, document.title);
    gtag("set", { page_location, page_title });
    gtag("event", "page_view", {
      page_location,
      page_path: redactPath(location.pathname),
      page_title,
    });
  });
}

export interface CtaClick {
  /** The button's rendered label. */
  link_text: string;
  /** Where the button sits on the page, e.g. "hero". */
  cta_placement: string;
  /** "returning" for signed-in users heading to their dashboard. */
  cta_state: "new" | "returning";
  link_url?: string;
}

/** The same seo_cta_click the static pages send (scripts/seo-core.ts). */
export function trackCta({ link_url, ...params }: CtaClick): void {
  withGtag((gtag) => {
    gtag("event", "seo_cta_click", {
      page_slug: redactPath(window.location.pathname),
      transport_type: "beacon",
      ...params,
      ...(link_url === undefined ? {} : { link_url: redactIds(link_url) }),
    });
  });
}

const BOT_ADDED = "bot_added";

/** Whether a query string carries the completed-invite marker, ?bot_added=1. */
export function hasBotAdded(search: string): boolean {
  return new URLSearchParams(search).get(BOT_ADDED) === "1";
}

/**
 * GA4's own video event names — the ones enhanced measurement sends for
 * embedded YouTube players — so plays of a self-hosted video land in the same
 * reports and need no custom definitions.
 */
export type VideoEvent = "video_start" | "video_progress" | "video_complete";

export interface VideoPlayback {
  video_title: string;
  /** Seconds, rounded. */
  video_duration: number;
  video_current_time: number;
  /** 0–100; for video_progress, the milestone just crossed. */
  video_percent: number;
}

export function trackVideo(event: VideoEvent, params: VideoPlayback): void {
  withGtag((gtag) => {
    gtag("event", event, { video_provider: "self-hosted", ...params });
  });
}

/** `search` without the bot_added marker; returned as-is when it has none. */
export function withoutBotAdded(search: string): string {
  const params = new URLSearchParams(search);
  if (!params.has(BOT_ADDED)) return search;
  params.delete(BOT_ADDED);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

/**
 * A completed bot invite: the API appends ?bot_added=1 to the redirect to the
 * new server's dashboard. Sent without parameters; the page it fires on is
 * already in page_location, redacted.
 */
export function trackBotAdded(): void {
  withGtag((gtag) => gtag("event", "bot_added"));
}
